import { BRAND_MODES, FOOD_AMIGOS_BRAND, NEUTRAL_BRAND } from './brands';

const SERVICE_PROVIDER_BRAND_FIELDS = [
  'name',
  'logo_url',
  'circle_logo_url',
  'splash_image_url',
  'primary_color',
  'dashboard_subdomain',
];

/**
 * The partner's languages, primary first. The brand-config / current-brand payloads
 * carry `language` + `languages`; the raw provider row carries the `language` snapshot
 * plus its region's `locales`. Empty when neither is known (callers fall back).
 */
function brandLanguagesFromSource(source) {
  const languages = source.languages ?? source.regional_config?.locales ?? [];
  const language = source.language ?? languages[0] ?? null;

  return {
    language,
    languages: [...new Set([language, ...languages].filter(Boolean))],
  };
}

function partnerBrand(source) {
  return {
    mode: BRAND_MODES.PARTNER,
    name: source.name ?? NEUTRAL_BRAND.name,
    logoUrl: source.logo_url ?? null,
    circleLogoUrl: source.circle_logo_url ?? null,
    splashImageUrl: source.splash_image_url ?? null,
    primaryColor: source.primary_color ?? NEUTRAL_BRAND.primaryColor,
    ...brandLanguagesFromSource(source),
  };
}

/**
 * Brand of a logged-in session, derived from the raw `service_providers` row
 * held in `state.currentServiceProvider`. The master provider IS the
 * white-labeling platform, so it always gets the FoodAmigos identity.
 * `null` while no provider is known (the caller keeps its previous brand).
 */
export function brandFromServiceProvider(serviceProvider) {
  if (!serviceProvider) {
    return null;
  }

  if (serviceProvider.is_master) {
    return FOOD_AMIGOS_BRAND;
  }

  return partnerBrand(serviceProvider);
}

/**
 * Brand of a logged-out screen, from `GET /brand-config?partner=<slug>`.
 *
 * `partnerId` is carried when the payload names the partner (`id`), so a failed
 * login on its page can be attributed to it — analytics only, not cached. Whether
 * the partner is a test one is not read here: the public endpoint does not say,
 * since anyone could probe slugs with it.
 */
export function brandFromConfigResponse(data) {
  if (!data) {
    return NEUTRAL_BRAND;
  }

  const namesPartner = !data.is_neutral && Number.isInteger(data.id);

  return {
    ...partnerBrand(data),
    mode: data.is_neutral ? BRAND_MODES.NEUTRAL : BRAND_MODES.PARTNER,
    ...(namesPartner ? { partnerId: data.id } : {}),
  };
}

/**
 * The subset of a brand resource (`/partner-settings/brand`,
 * `/service-provider-brand`) that also lives on the raw provider row — used to
 * patch `state.currentServiceProvider` after a partner edits its brand.
 */
export function pickServiceProviderBrandFields(resource) {
  if (!resource) {
    return {};
  }

  return SERVICE_PROVIDER_BRAND_FIELDS.reduce((fields, key) => {
    if (key in resource) {
      fields[key] = resource[key];
    }

    return fields;
  }, {});
}
